// Products.jsx — clean rebuild (2026-02-25).
//
// The user explicitly asked for a full rewrite of this page. Previous
// versions accumulated 4-5 child panels (range/timeline/delta/daily-
// consumption) which duplicated each other and exposed system-level
// data-gap warnings to end users. This file collapses everything into
// THREE focused sections, all tied to the **global date range** picker
// at the top of the dashboard (passed via the `prods` prop which the
// parent already filters by the selected period):
//
//   1️⃣  📦 المنتجات          → KPIs + Hot/Cold split + top table
//   2️⃣  🤖 تحليل المنتجات الذكي → per-product AI deep dive (Claude)
//   3️⃣  🔮 توقع مبيعات المنتجات → day/week/month forecast with backtest accuracy
//
// Data-quality issues + raw daily consumption breakdown have been
// MOVED to the admin-only «📋 سجل تحسين النظام» tab so end-users see
// a clean, professional report.
import React, { useEffect, useMemo, useState } from "react";
import { fm } from "./lib/analytics";
import { mkC } from "./lib/chartFactory";
import { Kpi } from "./Dashboard";
import { downloadCSV } from "./lib/export";
import HSegBar from "./components/HSegBar";
import ProductsAdvisorPanel from "./ProductsAdvisorPanel";
import ProductsForecastPanel from "./ProductsForecastPanel";
import ProductAvailabilityPanel from "./ProductAvailability";

const FILTERS = [
  { id: "all",    label: "📋 الكل" },
  { id: "stars",  label: "⭐ النجوم" },
  { id: "losers", label: "🚨 خاسرة" },
  { id: "opp",    label: "💡 فرص مهملة" },
];

function classifyProd(p) {
  if (p.profit < 0 && p.rev > 0) return "losers";
  if (p.rev > 500 && p.profit / p.rev > 0.5) return "stars";
  if (p.rev > 0 && p.profit / p.rev > 0.6 && p.qty < 30) return "opp";
  return "all";
}

export function Products({ prods, onUpload, user, dateFrom, dateTo, period }) {
  const [filter, setFilter] = useState("all");
  const [section, setSection] = useState("list"); // list | ai | forecast

  // Aggregate KPIs from the prods array (already filtered by global date range in parent)
  const totals = useMemo(() => {
    if (!prods?.length) return null;
    const totalRev    = prods.reduce((a, p) => a + (p.rev || 0), 0);
    const totalQty    = prods.reduce((a, p) => a + (p.qty || 0), 0);
    const totalProfit = prods.reduce((a, p) => a + (p.profit || 0), 0);
    const avgMargin   = totalRev > 0 ? (totalProfit / totalRev) * 100 : 0;
    const losers = prods.filter((p) => p.profit < 0 && p.rev > 0);
    const stars  = prods.filter((p) => p.rev > 500 && (p.profit / p.rev) > 0.5)
      .sort((a, b) => b.profit - a.profit);
    const opp    = prods.filter((p) => p.rev > 0 && (p.profit / p.rev) > 0.6 && p.qty < 30);
    const topQtyProd = [...prods].sort((a, b) => b.qty - a.qty)[0];
    return { totalRev, totalQty, totalProfit, avgMargin, losers, stars, opp, topQtyProd };
  }, [prods]);

  // Hot/Cold split for "قهوة اليوم" products only
  const tempAgg = useMemo(() => {
    const m = { hot: { qty: 0, rev: 0 }, cold: { qty: 0, rev: 0 } };
    (prods || []).filter((p) => String(p.name || "").includes("قهوة اليوم")).forEach((p) => {
      const isCold = String(p.name || "").includes("بارد");
      const bucket = isCold ? "cold" : "hot";
      m[bucket].qty += Number(p.qty) || 0;
      m[bucket].rev += Number(p.rev) || 0;
    });
    return m;
  }, [prods]);

  useEffect(() => {
    if (section !== "list" || !prods?.length) return;
    const t10r = [...prods].sort((a, b) => b.rev - a.rev).slice(0, 10);
    mkC("c-pr-rev", "bar", t10r.map((p) => p.name.slice(0, 14)), [{
      label: "الإيراد", data: t10r.map((p) => Math.round(p.rev)),
      backgroundColor: t10r.map((p) => (p.profit < 0 ? "rgba(220,38,38,.78)" : "rgba(16,185,129,.78)")),
      borderRadius: 6,
    }], { plugins: { legend: { display: false } }, indexAxis: "y" });
  }, [prods, section]);

  const filtered = useMemo(() => {
    if (!prods) return [];
    if (filter === "all") return [...prods].sort((a, b) => b.rev - a.rev);
    return prods.filter((p) => classifyProd(p) === filter).sort((a, b) => b.rev - a.rev);
  }, [prods, filter]);

  if (!prods?.length) {
    return (
      <div data-testid="products-empty">
        <div className="card" style={{ textAlign: "center", padding: 30 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📦</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown)", marginBottom: 6 }}>
            ارفع تقرير المنتجات
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
            ارفع التقارير الدورية من رتم لتفعيل لوحات التحليل والتنبؤ
          </div>
          <button data-testid="upload-products-btn" className="btn btn-soft" onClick={onUpload}>
            📦 رفع تقرير المنتجات
          </button>
        </div>
      </div>
    );
  }

  const SECTIONS = [
    { id: "list",     label: "📦 المنتجات",            color: "#6B4423" },
    { id: "ai",       label: "🤖 تحليل المنتجات الذكي", color: "#7C3AED" },
    { id: "forecast", label: "🔮 توقع مبيعات المنتجات",  color: "#0891B2" },
    { id: "avail",    label: "🚦 توقّف المنتجات",        color: "#DC2626" },
  ];

  return (
    <div data-testid="products-page">
      {/* Section pills — keeps the page focused; each section is a single deep view */}
      <div data-testid="pr-sec-tabs" style={{
        display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14, padding: 8,
        background: "#fff", borderRadius: 8, border: "1px solid var(--border)",
      }}>
        {SECTIONS.map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`pr-sec-${s.id}`}
            style={{
              padding: "8px 16px", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 800,
              cursor: "pointer", flex: "1 1 200px", minWidth: 0,
              background: section === s.id ? s.color : "transparent",
              color: section === s.id ? "#fff" : s.color,
              transition: "all .15s ease",
            }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ════════ Section 1: Products list ════════ */}
      {section === "list" && totals && (
        <div data-testid="pr-section-list">
          <Kpi items={[
            { l: "💰 إجمالي الإيراد",
              v: `${fm(Math.round(totals.totalRev))} ر`,
              s: `${prods.length} منتج · ${fm(Math.round(totals.totalQty))} وحدة` },
            { l: "🏆 إجمالي الربح",
              v: `${fm(Math.round(totals.totalProfit))} ر`,
              s: `هامش ${totals.avgMargin.toFixed(1)}%`,
              c: totals.totalProfit < 0 ? "kc-danger" : "" },
            { l: "🚨 منتجات بخسارة",
              v: `${totals.losers.length}`,
              s: totals.losers.length
                ? totals.losers.slice(0, 2).map((l) => l.name).join("، ")
                : "ممتاز — لا توجد خسائر",
              c: totals.losers.length > 0 ? "kc-danger" : "" },
            { l: "⭐ منتجات نجمة",
              v: `${totals.stars.length}`,
              s: totals.stars.length ? `أفضل: ${totals.stars[0]?.name}` : "—" },
          ]} />

          {/* Hot / Cold split — only meaningful when قهوة اليوم has been sold */}
          {(tempAgg.hot.qty + tempAgg.cold.qty) > 0 && (
            <div className="card" style={{ padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>
                ☕ حار vs بارد — قهوة اليوم
              </div>
              <HSegBar
                segments={[
                  { label: "حار 🔥", value: tempAgg.hot.qty, color: "#DC2626", sub: `${fm(Math.round(tempAgg.hot.rev))} ر` },
                  { label: "بارد ❄", value: tempAgg.cold.qty, color: "#0891B2", sub: `${fm(Math.round(tempAgg.cold.rev))} ر` },
                ]}
              />
            </div>
          )}

          {/* Top-10 chart */}
          <div className="card" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>
              📊 أعلى 10 منتجات بالإيراد
            </div>
            <div style={{ height: 320 }}><canvas id="c-pr-rev" data-testid="pr-chart-rev"></canvas></div>
          </div>

          {/* Filters + table */}
          <div className="card" style={{ padding: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
              flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FILTERS.map((f) => (
                  <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`pr-filter-${f.id}`}
                    style={{
                      padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                      borderRadius: 6, border: "1px solid var(--brown-deep)",
                      background: filter === f.id ? "var(--brown-deep)" : "#fff",
                      color: filter === f.id ? "#fff" : "var(--brown-deep)",
                    }}>
                    {f.label}
                  </button>
                ))}
              </div>
              <button onClick={() => downloadCSV(`products-${filter}.csv`, filtered)} data-testid="pr-csv-btn"
                style={{ padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                  borderRadius: 6, background: "var(--brown-deep)", color: "#fff", border: "none" }}>
                ⬇ تنزيل CSV
              </button>
            </div>
            <div className="tw" style={{ maxHeight: 480, overflowY: "auto" }}>
              <table data-testid="pr-table">
                <thead>
                  <tr>
                    <th>#</th><th>المنتج</th><th>الكمية</th>
                    <th>الإيراد</th><th>الربح</th><th>الهامش</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 100).map((p, i) => {
                    const margin = p.rev > 0 ? (p.profit / p.rev) * 100 : 0;
                    return (
                      <tr key={i}>
                        <td>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td>{fm(Math.round(p.qty || 0))}</td>
                        <td>{fm(Math.round(p.rev || 0))} ر</td>
                        <td style={{ color: p.profit < 0 ? "#DC2626" : "#16A34A", fontWeight: 700 }}>
                          {fm(Math.round(p.profit || 0))} ر
                        </td>
                        <td style={{ color: margin < 0 ? "#DC2626" : margin > 50 ? "#16A34A" : "var(--text)" }}>
                          {margin.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filter !== "all" && filtered.length === 0 && (
              <div style={{ padding: 16, textAlign: "center", color: "var(--muted)" }}>
                لا توجد منتجات في هذه الفئة
              </div>
            )}
          </div>
        </div>
      )}

      {/* ════════ Section 2: AI Deep Analysis ════════ */}
      {section === "ai" && (
        <div data-testid="pr-section-ai">
          <ProductsAdvisorPanel user={user} />
        </div>
      )}

      {/* ════════ Section 3: Sales Forecast ════════ */}
      {section === "forecast" && (
        <div data-testid="pr-section-forecast">
          <ProductsForecastPanel />
        </div>
      )}

      {/* ════════ Section 4: Availability (v119.78) ════════ */}
      {section === "avail" && (
        <div data-testid="pr-section-avail">
          <ProductAvailabilityPanel />
        </div>
      )}
    </div>
  );
}
